import { Fragment, PropsWithChildren } from 'react';
import toast from 'react-hot-toast';
import { When } from 'react-if';
import copy from 'copy-to-clipboard';
import { format } from 'date-fns';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { match } from 'ts-pattern';
import { useCancelPurchaseOnetimePlan, ZepApiError, ZepApiStatusType } from '@zep/apis';
import { logger } from '@zep/core-logger';
import { useDialog } from '@zep/dialog';
import { ClockIcon, RotateCircleIcon, SquareStackIcon } from '@zep/icons';
import { CountryCode, Currency, LabelStatus } from '@zep/types';
import { Button, IconButton, Stack } from '@zep/ui';
import { RedDefault, White } from '@zep/ui-tokens/color';
import { cn } from '@zep/utils';
import { useCancelDowngrade, useCancelUnsubscribe } from '../../../../services';
import { BankCodes, OperationType, PlanType, ReadySpacePlanWithVirtualAccountDto, SpacePlanDto, SubscribePlan, VirtualAccountDto } from '../../../../types/SubscriptionType';
import { fCurrency, fDateByCountryCode, fDateTimeByCountryCode } from '../../../../utils';
import { useGoSubscriptionPayment } from '../hooks';
import { usePlanSpaceDetailContext } from '../plan';
import { AdditionalLabel } from '../space';
import { PlanSpaceGroup } from '../space/organisms';
import S from './AppliedPlan.module.scss';
import { PlanTypeLabel } from './PlanTypeLabel';
export const CurrentPlanActive = () => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    spacePlan,
    readySpacePlans,
    subscription,
    space,
    nextSpacePlanTemplate
  } = usePlanSpaceDetailContext();
  const ButtonStatus: 'Cancel' | 'Error' | 'Active' = (() => {
    if (subscription?.requestedAction === 'UNSUBSCRIBE') return 'Cancel';
    // 결제실패의 경우 paymentFailCount 가 0 초과의 값이라면 결제 실패 상태.
    if ((spacePlan?.paymentFailCount ?? -1) > 0) return 'Error';
    return 'Active';
  })();
  return <>
      <PlanSpaceGroup title={t('subscription:SpaceDetail.menuManage.currentPlan')} data-sentry-element="PlanSpaceGroup" data-sentry-source-file="CurrentPlanActive.tsx">
        <Stack gap={8} style={{
        justifyContent: 'center',
        alignItems: 'flex-start'
      }} data-sentry-element="Stack" data-sentry-source-file="CurrentPlanActive.tsx">
          <div className="flex items-center gap-[6px]">
            <PlanTypeLabel planType={spacePlan?.planType} maxCcu={spacePlan?.maxCcu} data-sentry-element="PlanTypeLabel" data-sentry-source-file="CurrentPlanActive.tsx" />
            {ButtonStatus === 'Error' && <AdditionalLabel status={LabelStatus.FAIL} />}
          </div>
          {match(ButtonStatus).with('Cancel', () => <IsCancel />).with('Error', () => <IsError />).with('Active', () => <IsActive />).exhaustive()}
        </Stack>
      </PlanSpaceGroup>
      <When condition={subscription?.requestedAction === 'DOWNGRADE' || !!nextSpacePlanTemplate} data-sentry-element="When" data-sentry-source-file="CurrentPlanActive.tsx">
        <ReservedPlanGroup dateTime={spacePlan?.endDate ?? new Date().toString()} countryCode={space?.countryCode} maxCcu={nextSpacePlanTemplate?.maxCcu} planType={nextSpacePlanTemplate?.planType} data-sentry-element="ReservedPlanGroup" data-sentry-source-file="CurrentPlanActive.tsx" />
      </When>
      <When condition={readySpacePlans.length} data-sentry-element="When" data-sentry-source-file="CurrentPlanActive.tsx">
        <ReadyPlanGroup readySpacePlans={readySpacePlans} countryCode={space?.countryCode} data-sentry-element="ReadyPlanGroup" data-sentry-source-file="CurrentPlanActive.tsx" />
      </When>
    </>;
};
const IsActive = () => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    spacePlan,
    subscription
  } = usePlanSpaceDetailContext();
  return <Stack gap={8} style={{
    alignItems: 'flex-start'
  }} data-sentry-element="Stack" data-sentry-component="IsActive" data-sentry-source-file="CurrentPlanActive.tsx">
      {!(spacePlan?.operationType === OperationType.커스텀_일시납 || subscription?.requestedAction === 'DOWNGRADE') && <>
          <label className={S.plan_upgrade_message}>
            {t('subscription:SpaceDetail.menuManage.currentPlanDescription')}
          </label>
          <UpdatePlanButton />
        </>}
    </Stack>;
};
const UpdatePlanButton = () => {
  const move = useGoSubscriptionPayment();
  const {
    t
  } = useTranslation(['subscription']);
  const {
    spacePlan
  } = usePlanSpaceDetailContext();
  const handleClickPlanChange = () => {
    logger.track('home_subscription_plan_upgrade', {
      place: 'details'
    });
    if (spacePlan == null) return;
    move({
      planType: spacePlan.planType.toLowerCase() as SubscribePlan,
      spaceHashId: spacePlan.spaceHashId
    });
  };
  return <Button size={'xs'} leftSlot={<RotateCircleIcon width={14} height={14} fill={White} />} onClick={handleClickPlanChange} data-sentry-element="Button" data-sentry-component="UpdatePlanButton" data-sentry-source-file="CurrentPlanActive.tsx">
      {t('subscription:SpaceDetail.menuManage.currentPlanChange')}
    </Button>;
};
const IsError = () => {
  const {
    t
  } = useTranslation(['subscription']);
  return <label className={S.label_error} data-sentry-component="IsError" data-sentry-source-file="CurrentPlanActive.tsx">
      {t('subscription:SpaceDetail.menuManage.messagePaymentFail')}
    </label>;
};
const IsCancel = () => {
  const {
    reload
  } = useRouter();
  const dialog = useDialog();
  const {
    t
  } = useTranslation(['subscription']);
  const {
    spacePlan,
    space
  } = usePlanSpaceDetailContext();
  const date = new Date(spacePlan?.endDate ?? new Date().toString());
  const {
    mutateAsync: cancelUnsubscribeMutateAsync
  } = useCancelUnsubscribe(spacePlan?.spaceHashId ?? '');
  const handleClickWithdraw = async () => {
    const ok = await checkCancelUnsubscribe();
    if (!ok) return;
    try {
      await cancelUnsubscribeMutateAsync();
      reload();
    } catch (error) {
      console.error(error);
      if (error instanceof ZepApiError) {
        switch (error.status) {
          case ZepApiStatusType.PLAN_ALREADY_EXISTS:
            {
              await dialog.alert({
                title: t('subscription:SpaceDetail.cancelUnsubscribe.impossibleTitle'),
                content: t('subscription:SpaceDetail.cancelUnsubscribe.impossibleContent')
              });
              return;
            }
        }
      }
      await dialog.alert({
        title: t('subscription:SpaceDetail.cancelUnsubscribe.alert'),
        confirmText: t('subscription:payment.alertFailCardRegister.confirm')
      });
    }
  };
  const checkCancelUnsubscribe = async () => {
    return await dialog.confirm({
      title: t('subscription:SpaceDetail.cancelUnsubscribe.title'),
      content: t('subscription:SpaceDetail.cancelUnsubscribe.description'),
      confirmText: t('subscription:payment.alertFailCardRegister.confirm'),
      cancelText: t('subscription:payment.alertCancel.buttonCancel')
    });
  };
  return <>
      <label className={cn(S.label, 'text-[#27262ea3]')}>
        {t('subscription:SpaceDetail.menuManage.currentPlanExpiredNotificaiton', {
        expiredDate: fDateByCountryCode(date, space.countryCode)
      })}
      </label>
      <Button variant={'sub'} size={'xs'} onClick={handleClickWithdraw} data-sentry-element="Button" data-sentry-source-file="CurrentPlanActive.tsx">
        {t('subscription:SpaceDetail.cancelUnsubscribe.title')}
      </Button>
    </>;
};
export const ReservedPlanGroup = ({
  planType,
  maxCcu,
  dateTime,
  countryCode
}: {
  planType?: PlanType;
  maxCcu?: number;
  dateTime: string;
  countryCode: CountryCode;
}) => {
  const {
    t
  } = useTranslation(['subscription']);
  const router = useRouter();
  const {
    mutateAsync: 플랜변경취소
  } = useCancelDowngrade(router.query.id as string);
  const dialog = useDialog();
  const handleClickCancel = async () => {
    const ok = await dialog.confirm({
      title: t('subscription:plans.detail.planChangeCancel'),
      content: t('subscription:plans.detail.planChangeCancelDescription'),
      confirmText: t('subscription:plans.detail.cancelChangeButton'),
      cancelText: t('subscription:payment.alertCancel.buttonCancel'),
      confirmVariant: 'alert'
    });
    if (!ok) return;
    try {
      await 플랜변경취소();
      router.reload();
    } catch (error) {
      console.error(error);
      if (error instanceof ZepApiError && error.status === ZepApiStatusType.NOT_FOUND_BILLING_KEY) {
        await dialog.alert({
          title: t('subscription:SpaceDetail.cantPlanChange.title'),
          content: t('subscription:SpaceDetail.cantPlanChange.content')
        });
        return;
      }
      await dialog.alert({
        title: t('subscription:plans.detail.planChangeFailedTitle'),
        confirmText: t('subscription:plans.detail.cancelChangeFailedDescription')
      });
    }
  };
  return <PlanSpaceGroup title={t('subscription:SpaceDetail.menuManage.reservedPlanSubTitle')} data-sentry-element="PlanSpaceGroup" data-sentry-component="ReservedPlanGroup" data-sentry-source-file="CurrentPlanActive.tsx">
      <Stack gap={8} data-sentry-element="Stack" data-sentry-source-file="CurrentPlanActive.tsx">
        <PlanTypeLabel planType={planType} maxCcu={maxCcu} data-sentry-element="PlanTypeLabel" data-sentry-source-file="CurrentPlanActive.tsx" />
        <div className={S.plan_upgrade_message}>
          {t('subscription:SpaceDetail.menuManage.reservedPlanText', {
          date: fDateTimeByCountryCode(dateTime, countryCode)
        })}
        </div>
        <Button style={{
        width: 'fit-content'
      }} size={'xs'} variant={'alert'} onClick={handleClickCancel} data-sentry-element="Button" data-sentry-source-file="CurrentPlanActive.tsx">
          {t('subscription:plans.detail.planChangeCancel')}
        </Button>
      </Stack>
    </PlanSpaceGroup>;
};
export const ReadyPlanGroup = ({
  readySpacePlans,
  countryCode
}: {
  readySpacePlans: ReadySpacePlanWithVirtualAccountDto[];
  countryCode: CountryCode;
}) => {
  const {
    t
  } = useTranslation(['subscription']);
  return <PlanSpaceGroup title={t('subscription:SpaceDetail.menuManage.reservedPlanSubTitle', '변경 예정 플랜')} data-sentry-element="PlanSpaceGroup" data-sentry-component="ReadyPlanGroup" data-sentry-source-file="CurrentPlanActive.tsx">
      <div className="flex flex-col gap-[20px]">
        {readySpacePlans.map((item, index, arr) => <Fragment key={item.readySpacePlan.spacePlanHashId}>
            <Stack className="w-[340px]" gap={8}>
              <ReadySpacePlanTitle planType={item.readySpacePlan.planType} maxCcu={item.readySpacePlan.maxCcu} isWaitingForPayment={!!item.virtualAccountInfo} />
              <div className={S.plan_upgrade_message}>
                {t('subscription:SpaceDetail.hasReady.description', {
              date: fDateTimeByCountryCode(item.readySpacePlan.startDate, countryCode)
            })}
              </div>
              <ReadySpacePlanPeriod readySpacePlan={item.readySpacePlan} />
              {item.virtualAccountInfo && <ReadySpacePlanVirtualAccount readySpacePlan={item.readySpacePlan} virtualAccountInfo={item.virtualAccountInfo} />}
            </Stack>
            {index !== arr.length - 1 && <hr className="m-0 h-px border-gray-100" />}
          </Fragment>)}
      </div>
    </PlanSpaceGroup>;
};
const ReadySpacePlanTitle = ({
  planType,
  maxCcu,
  isWaitingForPayment
}: {
  planType: PlanType;
  maxCcu: number;
  isWaitingForPayment: boolean;
}) => {
  return <div className="flex items-center gap-[6px]" data-sentry-component="ReadySpacePlanTitle" data-sentry-source-file="CurrentPlanActive.tsx">
      <PlanTypeLabel planType={planType} maxCcu={maxCcu} data-sentry-element="PlanTypeLabel" data-sentry-source-file="CurrentPlanActive.tsx" />
      <AdditionalLabel status={isWaitingForPayment ? LabelStatus.WAITING_FOR_PAYMENT : LabelStatus.READY} data-sentry-element="AdditionalLabel" data-sentry-source-file="CurrentPlanActive.tsx" />
    </div>;
};
const ReadySpacePlanCard = ({
  children
}: PropsWithChildren) => <div className="border-[rgba(39, 38, 46, 0.08)] flex flex-col gap-[8px] rounded-[10px] border bg-white px-[20px] py-[18px] shadow-sm" data-sentry-component="ReadySpacePlanCard" data-sentry-source-file="CurrentPlanActive.tsx">
    {children}
  </div>;
const ReadySpacePlanCardLabel = ({
  children
}: PropsWithChildren) => <span className="text-body-2 font-medium text-[#27262ea3]" data-sentry-component="ReadySpacePlanCardLabel" data-sentry-source-file="CurrentPlanActive.tsx">{children}</span>;
const ReadySpacePlanPeriod = ({
  readySpacePlan
}: {
  readySpacePlan: SpacePlanDto;
}) => {
  const {
    t
  } = useTranslation(['subscription']);
  return <ReadySpacePlanCard data-sentry-element="ReadySpacePlanCard" data-sentry-component="ReadySpacePlanPeriod" data-sentry-source-file="CurrentPlanActive.tsx">
      <div className="flex items-center justify-between">
        <ReadySpacePlanCardLabel data-sentry-element="ReadySpacePlanCardLabel" data-sentry-source-file="CurrentPlanActive.tsx">
          {t('subscription:SpaceDetail.menuManage.readyCardStartDate', '플랜 시작일')}
        </ReadySpacePlanCardLabel>
        <span className="text-body-2 font-medium text-[#27262ecc]">
          {format(new Date(readySpacePlan.startDate), 'yyyy.MM.dd HH:mma')}
        </span>
      </div>

      <div className="flex items-center justify-between">
        <ReadySpacePlanCardLabel data-sentry-element="ReadySpacePlanCardLabel" data-sentry-source-file="CurrentPlanActive.tsx">
          {t('subscription:SpaceDetail.menuManage.readyCardEndDate', '만료일')}
        </ReadySpacePlanCardLabel>
        <span className="text-body-2 font-medium text-[#27262ecc]">
          {format(new Date(readySpacePlan.endDate), 'yyyy.MM.dd HH:mma')}
        </span>
      </div>
    </ReadySpacePlanCard>;
};
const ReadySpacePlanVirtualAccount = ({
  readySpacePlan,
  virtualAccountInfo
}: {
  readySpacePlan: SpacePlanDto;
  virtualAccountInfo: VirtualAccountDto;
}) => {
  const router = useRouter();
  const {
    t
  } = useTranslation(['subscription']);
  const dialog = useDialog();
  const {
    mutateAsync
  } = useCancelPurchaseOnetimePlan();
  const {
    refetchPlanDetails
  } = usePlanSpaceDetailContext();
  const handleClickCopyButton = () => {
    const success = copy(virtualAccountInfo.accountNumber);
    if (success) {
      toast.success(t('subscription:SpaceDetail.menuManage.virtualAccountNumberCopied', {
        defaultValue: '계좌 번호가 복사되었습니다'
      }), {
        position: 'top-center'
      });
    }
  };
  const handleClickCancelButton = async () => {
    const confirm = await dialog.confirm({
      title: t('subscription:SpaceDetail.menuManage.virtualCancelAlertTitle', {
        defaultValue: '가상 계좌 결제 취소'
      }),
      content: t('subscription:SpaceDetail.menuManage.virtualCancelAlertContent', {
        planType: readySpacePlan.planType,
        maxCcu: readySpacePlan.maxCcu,
        defaultValue: `${readySpacePlan.planType} ${readySpacePlan.maxCcu} 플랜의 가상 계좌(무통장 입금) 결제를 취소하시겠습니까?`
      }),
      confirmText: t('subscription:SpaceDetail.menuManage.virtualCancelAlertConform', {
        defaultValue: '결제 취소'
      }),
      confirmVariant: 'alert'
    });
    if (!confirm) return;
    await mutateAsync({
      spaceHashId: readySpacePlan.spaceHashId,
      spacePlanHashId: readySpacePlan.spacePlanHashId
    });
    await dialog.alert({
      title: t('subscription:SpaceDetail.menuManage.virtualCancelCompleteTitle', {
        defaultValue: '결제 취소 완료'
      }),
      content: t('subscription:SpaceDetail.menuManage.virtualCancelCompleteContent', {
        defaultValue: '가상 계좌 결제 취소가 완료되었습니다.'
      })
    });
    const {
      data
    } = await refetchPlanDetails();
    if (!data?.spacePlan) {
      router.replace('/me/spaces/plans');
    }
  };
  return <div className="mt-[2px] flex flex-col gap-[8px]" data-sentry-component="ReadySpacePlanVirtualAccount" data-sentry-source-file="CurrentPlanActive.tsx">
      <div className="flex items-center gap-[5px] text-body-2 font-medium text-gray-900">
        <span>
          <ClockIcon width={15} height={15} fill={RedDefault} data-sentry-element="ClockIcon" data-sentry-source-file="CurrentPlanActive.tsx" />
        </span>
        <span className="font-bold text-red">
          {t('subscription:SpaceDetail.menuManage.virtualCardDueDate', {
          date: format(new Date(virtualAccountInfo.dueDate), 'yyyy.MM.dd HH:mma'),
          defaultValue: `${format(new Date(virtualAccountInfo.dueDate), 'yyyy.MM.dd HH:mma')} 까지 미입금 시 자동 취소`
        })}
        </span>
      </div>

      <ReadySpacePlanCard data-sentry-element="ReadySpacePlanCard" data-sentry-source-file="CurrentPlanActive.tsx">
        <div className="flex items-center justify-between">
          <ReadySpacePlanCardLabel data-sentry-element="ReadySpacePlanCardLabel" data-sentry-source-file="CurrentPlanActive.tsx">
            {t('subscription:SpaceDetail.menuManage.virtualCardPaymentMethod', {
            defaultValue: '결제 수단'
          })}
          </ReadySpacePlanCardLabel>
          <span className="text-body-2 font-medium text-[#27262ecc]">
            {t('subscription:SpaceDetail.menuManage.virtualCardVirtualTransfer', {
            defaultValue: '가상 계좌(무통장 입금)'
          })}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <ReadySpacePlanCardLabel data-sentry-element="ReadySpacePlanCardLabel" data-sentry-source-file="CurrentPlanActive.tsx">
            {t('subscription:SpaceDetail.menuManage.virtualCardBankName', {
            defaultValue: '은행명'
          })}
          </ReadySpacePlanCardLabel>
          <span className="text-body-2 font-medium text-[#27262ecc]">
            {t('subscription:SpaceDetail.menuManage.virtualCardZepInfo', {
            bankName: BankCodes[virtualAccountInfo.bankCode as keyof typeof BankCodes],
            defaultValue: `${BankCodes[virtualAccountInfo.bankCode as keyof typeof BankCodes]} (예금주 : 주식회사 젭)`
          })}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <ReadySpacePlanCardLabel data-sentry-element="ReadySpacePlanCardLabel" data-sentry-source-file="CurrentPlanActive.tsx">
            {t('subscription:SpaceDetail.menuManage.virtualCardAccountNumber', {
            defaultValue: '계좌 번호'
          })}
          </ReadySpacePlanCardLabel>
          <div className="flex items-center gap-[4px] text-body-2 font-medium text-[#27262ecc]">
            <IconButton size="xxxs" variant="transparent" intent="sub" onClick={handleClickCopyButton} data-sentry-element="IconButton" data-sentry-source-file="CurrentPlanActive.tsx">
              <SquareStackIcon width={10} height={10} fill={'#27262EA3'} data-sentry-element="SquareStackIcon" data-sentry-source-file="CurrentPlanActive.tsx" />
            </IconButton>
            <span>{virtualAccountInfo.accountNumber}</span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <ReadySpacePlanCardLabel data-sentry-element="ReadySpacePlanCardLabel" data-sentry-source-file="CurrentPlanActive.tsx">
            {t('subscription:SpaceDetail.menuManage.virtualCardPrice', {
            defaultValue: '결제 금액'
          })}
          </ReadySpacePlanCardLabel>
          <span className="text-body-2 font-semibold text-gray-900">
            {/* 입금대기 금액은 가상계좌 발급 시점 서버 snapshot(finalPrice) 그대로.
                '(VAT 포함)' 문구는 currency가 아니라 persisted vatAmount > 0
                기준이어야 하므로(레거시 vat 0 건 오표기 방지, Gate E),
                vatAmount가 응답에 없는 현재는 금액만 표시한다. */}
            {fCurrency(virtualAccountInfo.finalPrice, virtualAccountInfo.currency as Currency, {
            position: 'pre'
          })}
          </span>
        </div>

        <Button size="xs" variant="sub" onClick={handleClickCancelButton} data-sentry-element="Button" data-sentry-source-file="CurrentPlanActive.tsx">
          {t('subscription:SpaceDetail.menuManage.virtualCardCancel', {
          defaultValue: '결제 취소'
        })}
        </Button>
      </ReadySpacePlanCard>

      <ul className="text-caption-1 font-normal text-[#27262e8a]">
        <li>
          {t('subscription:SpaceDetail.menuManage.virtualCardWarning1', {
          defaultValue: '• 입금 후 입금 확인까지 2분 이상의 시간이 소요될 수 있습니다.'
        })}
        </li>
        <li>
          {t('subscription:SpaceDetail.menuManage.virtualCardWarning2', {
          defaultValue: '• 해당 은행의 점검 시간인 경우 입금이 불가능할 수 있습니다.'
        })}
        </li>
        <li>
          {t('subscription:SpaceDetail.menuManage.virtualCardWarning3', {
          defaultValue: '• ZEP 서비스 점검 시간의 경우 일시적으로 입금 확인이 늦어질 수 있습니다. 점검 시간은 공지사항을 확인해주세요.'
        })}
        </li>
      </ul>
    </div>;
};